// tslint:disable:cognitive-complexity
import {
  firstDayOfMonthUTC,
  getDeliveryShopId,
  intervalISODateToDateRange,
} from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  DeliveryWindowType,
  OrderType,
} from 'graphql-api/gql-api-interfaces';

import {
  BulkTypeDeliveryPeriod,
  BusinessState,
  DataState,
  DeliveryItem,
  DeliveryPeriodType,
  ProductQuantitiesChange,
} from '../../../models';
import { getSelectedDeliveryPeriod } from '../../../shared/get-selected-delivery-period';
import { mapBulkDeliveryPeriodToFuture } from '../../../shared/map-bulk-delivery-to-future';
import { getDeliveryTypeByDate } from '../../../shared/shared';

import { calculateNewTotals } from './calculate-new-totals';
import { makeQuantitiesChange } from './make-quantity-change';

/** multiple quantities changes possibly across different shop and different delivery periods */
// tslint:disable-next-line: cognitive-complexity
export function productQuantitiesBusiness(
  state: BusinessState,
  { changes, data }: { changes: ProductQuantitiesChange[]; data: DataState },
): BusinessState {
  let newState = { ...state };
  const isAlreadyAddedToChanges = new Set<string>();
  let i = 0;

  while (i < changes.length) {
    const change = changes[i];
    const deliveryShopKey = change.accountAddressKey;
    const originalDeliveryPeriod = change.deliveryPeriod;
    const deliveryPeriodType: DeliveryPeriodType =
      change.deliveryPeriodType ??
      getDeliveryTypeByDate(originalDeliveryPeriod);

    let deliveryPeriod = mapBulkDeliveryPeriodToFuture(
      originalDeliveryPeriod,
      deliveryPeriodType,
    );
    let orderType = OrderType.Future;

    if (deliveryPeriod === OrderType.Now) {
      orderType = OrderType.Now;
    }

    const deliveryItems = { ...newState.deliveryItems };
    const deliveryShopItems = { ...deliveryItems[deliveryShopKey] };
    const deliveryPeriodItems = {
      ...deliveryShopItems[originalDeliveryPeriod],
    };
    const previousQuantity =
      deliveryPeriodItems?.items?.[change.productId]?.quantity ?? 0;
    const previousInDemandQuantity =
      deliveryPeriodItems?.items?.[change.productId]?.usedTrueDemandQuantity ??
      0;

    const currentDeliveryPeriods = newState.deliveryPeriods[deliveryShopKey];
    const currentDeliveryPeriodType =
      currentDeliveryPeriods?.currentDeliveryPeriodType as DeliveryPeriodType;

    if (
      orderType === OrderType.Future &&
      (deliveryPeriodType ?? currentDeliveryPeriodType) !==
        DeliveryPeriodType.Bulk &&
      data.product?.deliveryWindowType === DeliveryWindowType.Month
    ) {
      deliveryPeriod = firstDayOfMonthUTC(deliveryPeriod);
    }

    const { inventoryUpdate, usedInventories, inventories } =
      makeQuantitiesChange({
        state: newState,
        data,
        previousQuantity:
          deliveryPeriodItems?.items?.[change.productId]?.quantity ?? 0,
        quantity: change.quantity,
        productId: change.productId,
        deliveryShopKey,
        deliveryPeriodType: deliveryPeriodType ?? currentDeliveryPeriodType,
        deliveryPeriod,
        orderType,
      });

    /**
     * If previous 0, applied 0 and change is not 0
     * than it only means that we are applying inventories
     * to another delivery period (mostly) that does not
     * exist on it - meaning do not need to pollute state with 0
     */
    if (
      (deliveryPeriodItems?.items?.[change.productId]?.quantity ?? 0) === 0 &&
      inventoryUpdate.appliedQuantity === 0 &&
      change.quantity !== 0
    ) {
      i++;
      continue;
    }

    deliveryPeriodItems.orderType = orderType;
    deliveryPeriodItems.deliveryPeriodType = deliveryPeriodType;

    let consignmentStartDate;
    let consignmentEndDate;

    if (deliveryPeriodType === DeliveryPeriodType.Bulk) {
      consignmentStartDate = intervalISODateToDateRange(
        originalDeliveryPeriod,
      ).from;
      consignmentEndDate = intervalISODateToDateRange(
        originalDeliveryPeriod,
      ).to;
    }

    deliveryPeriodItems.items = {
      ...(deliveryPeriodItems?.items ?? {}),
      [change.productId]: {
        quantity: Math.max(0, inventoryUpdate.appliedQuantity),
        usedTrueDemandQuantity: Math.max(
          0,
          inventoryUpdate.appliedTrueDemandQuantity,
        ),
        note: change.note,
        availabilityDateFrom: inventories?.[0]?.availableFromDateUtc,
        availabilityDateTo: inventories?.[0]?.availableToDateUtc,
        useInventoryDate: !!inventories?.[0]?.displayFromDateUtc,
        displayDateFrom: inventories?.[0]?.displayFromDateUtc,
        consignmentStartDate,
        consignmentEndDate,
      },
    };

    deliveryItems[deliveryShopKey] = {
      ...deliveryItems[deliveryShopKey],
      [originalDeliveryPeriod]: deliveryPeriodItems,
    };

    // we need to consume new available inventories if we decrease quantity and some shop uses true demand
    if (
      orderType === OrderType.Now &&
      inventoryUpdate.appliedQuantity < previousQuantity &&
      inventoryUpdate.appliedTrueDemandQuantity === 0
    ) {
      Object.entries(deliveryItems).forEach(
        ([shopKey, deliveryItemsByPeriod]) => {
          if (shopKey !== deliveryShopKey) {
            Object.entries(deliveryItemsByPeriod[deliveryPeriod].items).forEach(
              ([productIdOther, value]) => {
                if (
                  change.productId === productIdOther &&
                  value.usedTrueDemandQuantity > 0
                ) {
                  const key = `${productIdOther}-${shopKey}-${deliveryPeriod}`;
                  if (!isAlreadyAddedToChanges.has(key)) {
                    changes.push({
                      productId: productIdOther,
                      accountAddressKey: shopKey,
                      quantity: value.quantity,
                      note: value.note,
                      deliveryPeriod,
                    });
                  }
                }
              },
            );
          }
        },
      );
    }

    const newTotals = calculateNewTotals({
      state: newState,
      data,
      productId: change.productId,
      // don't include in totals true demand inventories
      difference:
        inventoryUpdate.appliedQuantity -
        inventoryUpdate.appliedTrueDemandQuantity -
        (previousQuantity - previousInDemandQuantity),
      orderType,
    });

    newState = {
      ...newState,
      totalQuantity: newTotals.quantity,
      totalPrices: newTotals.prices,
      deliveryItems: {
        ...newState.deliveryItems,
        ...deliveryItems,
      },
      usedInventories,
      modifiedProducts: {
        ...newState.modifiedProducts,
        [deliveryShopKey]: {
          ...(newState.modifiedProducts?.[deliveryShopKey] ?? {}),
          [change.productId]: null,
        },
      },
    };

    i++;
  }

  return newState;
}

/** product quantity change for current delivery period for current store */
export function productQuantityChangeBusiness(
  state: BusinessState,
  {
    changes,
    deliveryShop,
    data,
  }: {
    changes: { productId: string; quantity: number }[];
    deliveryShop?: BasketAccountAddress;
    data: DataState;
  },
): BusinessState {
  // Changes must be transformed to an array with deliveryShop data and extended with changes from other shops
  // if current size quantity is decreased and there is items from true demand for other shop
  const deliveryShopKey = getDeliveryShopId(
    deliveryShop ?? state.activeDeliveryShop,
  );
  let clonedState = { ...state };

  const deliveryPeriod = getSelectedDeliveryPeriod(state);
  const deliveryItems = { ...clonedState.deliveryItems };
  const deliveryShopItems = { ...deliveryItems[deliveryShopKey] };
  const deliveryPeriodItems = { ...deliveryShopItems[deliveryPeriod] };

  const orderType =
    deliveryPeriod === OrderType.Now ? OrderType.Now : OrderType.Future;

  const currentDeliveryPeriods = state.deliveryPeriods[deliveryShopKey];
  const currentDeliveryPeriodType =
    currentDeliveryPeriods?.currentDeliveryPeriodType as DeliveryPeriodType;
  const futureDeliveryPeriodIfBulk = mapBulkDeliveryPeriodToFuture(
    deliveryPeriod,
    currentDeliveryPeriodType,
  );

  const trueDemandAdditionalChanges: ProductQuantitiesChange[] = [];

  changes.forEach(({ productId, quantity }) => {
    const previousQuantity =
      deliveryPeriodItems?.items?.[productId]?.quantity ?? 0;
    const previousInDemandQuantityQuantity =
      deliveryPeriodItems?.items?.[productId]?.usedTrueDemandQuantity ?? 0;
    const makeQuantitiesChangeResult = makeQuantitiesChange({
      state: clonedState,
      data,
      previousQuantity,
      quantity: quantity ?? 0,
      productId,
      deliveryShopKey,
      deliveryPeriodType: currentDeliveryPeriodType,
      deliveryPeriod: futureDeliveryPeriodIfBulk,
      // NEWLY ADDDED
      orderType,
    });

    const {
      inventories,
      usedInventories,
      inventoryUpdate: { appliedQuantity, appliedTrueDemandQuantity },
    } = makeQuantitiesChangeResult;

    deliveryPeriodItems.orderType = orderType;
    deliveryPeriodItems.deliveryPeriodType = currentDeliveryPeriodType;

    let deliveryItem: DeliveryItem = {
      quantity: appliedQuantity,
      usedTrueDemandQuantity:
        makeQuantitiesChangeResult.inventoryUpdate.appliedTrueDemandQuantity,
      availabilityDateFrom: inventories?.[0]?.availableFromDateUtc,
      availabilityDateTo: inventories?.[0]?.availableToDateUtc,
      displayDateFrom: inventories[0]?.displayFromDateUtc,
      useInventoryDate: !!inventories?.[0]?.displayFromDateUtc,
    };

    const deliveryItemNote = (
      (deliveryPeriodItems?.items ?? {})[productId] ?? {}
    ).note;
    if (deliveryItemNote) {
      deliveryItem = {
        ...deliveryItem,
        note: deliveryItemNote,
      };
    }

    deliveryPeriodItems.items = {
      ...(deliveryPeriodItems?.items ?? {}),
      [productId]: deliveryItem,
    };

    if (currentDeliveryPeriodType === DeliveryPeriodType.Bulk) {
      const { from, to } = currentDeliveryPeriods[DeliveryPeriodType.Bulk]
        ?.selectedDeliveryPeriod as BulkTypeDeliveryPeriod;
      deliveryPeriodItems.items = {
        ...(deliveryPeriodItems?.items ?? {}),
        [productId]: {
          ...((deliveryPeriodItems?.items ?? {})[productId] ?? {}),
          consignmentStartDate: from,
          consignmentEndDate: to,
        },
      };
    }

    deliveryItems[deliveryShopKey] = {
      ...deliveryItems[deliveryShopKey],
      [deliveryPeriod]: deliveryPeriodItems,
    };

    // we need to consume new available inventories if we decrease quantity and some shop uses true demand
    if (
      orderType === OrderType.Now &&
      appliedQuantity < previousQuantity &&
      appliedTrueDemandQuantity === 0
    ) {
      Object.entries(deliveryItems).forEach(
        ([shopKey, deliveryItemsByPeriod]) => {
          if (shopKey !== deliveryShopKey) {
            Object.entries(deliveryItemsByPeriod[deliveryPeriod].items).forEach(
              ([productIdOther, value]) => {
                if (
                  productId === productIdOther &&
                  value.usedTrueDemandQuantity > 0
                ) {
                  trueDemandAdditionalChanges.push({
                    productId,
                    accountAddressKey: shopKey,
                    quantity: value.quantity,
                    note: value.note,
                    deliveryPeriod,
                  });
                }
              },
            );
          }
        },
      );
    }

    const newTotals = calculateNewTotals({
      state: clonedState,
      data,
      productId,
      // we need to now the difference so we just modify total quantity instead of recalculating it
      // difference: appliedQuantity - previousQuantity,
      // don't include in totals true demand inventories
      difference:
        appliedQuantity -
        appliedTrueDemandQuantity -
        (previousQuantity - previousInDemandQuantityQuantity),
      orderType,
    });

    clonedState = {
      ...clonedState,
      totalQuantity: newTotals.quantity,
      totalPrices: newTotals.prices,
      deliveryItems: {
        ...clonedState.deliveryItems,
        ...deliveryItems,
      },
      usedInventories,
      modifiedProducts: {
        ...clonedState.modifiedProducts,
        [deliveryShopKey]: {
          ...((clonedState.modifiedProducts
            ? clonedState.modifiedProducts[deliveryShopKey]
            : {}) ?? {}),
          [productId]: null,
        },
      },
      dirty: true,
    };
  });

  if (trueDemandAdditionalChanges.length) {
    // consume quantity changes from other shops
    return productQuantitiesBusiness(clonedState, {
      changes: trueDemandAdditionalChanges,
      data,
    });
  }

  return clonedState;
}
